/**
 * Перенос `lib/src/utils/interceptors/diagnostics_interceptor.dart`.
 *
 * Пишет каждый запрос в [DiagnosticsLog]: метод, адрес без query, статус,
 * длительность. Работает и в release — журнал смотрит разработчик на самом
 * планшете.
 *
 * Отдельно ловит ответы ноды вида `200 {"code": -1, "msg": "…"}`: нода так
 * отказывает (персонаж не найден, нет активной сессии), а по статусу это
 * успех — без этой отметки отказ не виден нигде.
 */

import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import type { HttpError } from '@/utils/exceptions/appException';
import type { HttpInterceptor, HttpRequest, HttpResponse } from '@/utils/http/types';
import { asRecord, httpErrorMessage, requestUri, requestWithExtra } from '@/utils/http/types';

const STARTED_AT_KEY = '_diagnosticsStartedAt';

export class DiagnosticsInterceptor implements HttpInterceptor {
  constructor(log?: DiagnosticsLog) {
    this.#log = log ?? null;
  }

  readonly #log: DiagnosticsLog | null;

  get log(): DiagnosticsLog {
    return this.#log ?? DiagnosticsLog.instance;
  }

  onRequest = (request: HttpRequest): HttpRequest =>
    requestWithExtra(request, STARTED_AT_KEY, Date.now());

  onResponse = (response: HttpResponse): HttpResponse => {
    const request = response.request;
    this.log.request({
      method: request.method,
      uri: requestUri(request),
      status: response.statusCode,
      durationMs: elapsed(request),
      note: rejectedBody(response.data),
    });
    return response;
  };

  onError = (error: HttpError, request: HttpRequest): HttpError => {
    const response = error.response;
    this.log.request({
      method: request.method,
      uri: requestUri(request),
      status: response?.status,
      durationMs: elapsed(request),
      error:
        response === undefined
          ? `${error.type}: ${causeText(error) ?? httpErrorMessage(error)}`
          : (serverText(response.data) ?? error.type),
    });
    return error;
  };
}

function elapsed(request: HttpRequest): number | undefined {
  const startedAt = request.extra[STARTED_AT_KEY];
  if (typeof startedAt !== 'number') return undefined;
  return Date.now() - startedAt;
}

/**
 * `'${err.type.name}: ${err.error ?? err.message ?? ''}'` из Dart.
 *
 * `err.error` — исходная причина; пустой она в Dart быть могла, и тогда
 * в текст шло `message`. Здесь тот же порядок.
 */
function causeText(error: HttpError): string | undefined {
  const cause = error.cause;
  if (cause === null || cause === undefined) return undefined;
  return String(cause);
}

/** `{"code": -1, "msg": "…"}` при 200 — отказ ноды. */
function rejectedBody(data: unknown): string | undefined {
  const map = asRecord(data);
  if (map === null) return undefined;
  const code = map['code'];
  if (typeof code !== 'number' || !Number.isInteger(code) || code === 0) return undefined;
  const msg = map['msg'] ?? map['message'] ?? map['detail'];
  return `ответ 200, но code=${code}${typeof msg === 'string' ? `: ${msg}` : ''}`;
}

/** Текст ошибки из тела: `detail`/`message` бекенда, `error.message` шлюза. */
function serverText(data: unknown): string | undefined {
  const map = asRecord(data);
  if (map === null) {
    return typeof data === 'string' && data.length > 0 ? data : undefined;
  }
  const error = map['error'];
  const errorMap = asRecord(error);
  const errorText = errorMap !== null ? errorMap['message'] : error;
  for (const text of [map['detail'], map['message'], map['msg'], errorText]) {
    if (typeof text === 'string' && text.trim().length > 0) return text.trim();
  }
  return undefined;
}
